import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@epoch-ui/lib/utils';

const buttonGroupVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-border-border tw-border-[1.5px] tw-bg-background-grey tw-border-solid tw-rounded-xl tw-text-sm tw-font-medium disabled:tw-pointer-events-none disabled:tw-opacity-50 data-[state=checked]:tw-bg-light-blue data-[state=checked]:tw-text-primary-blue data-[state=checked]:tw-border-primary-blue',
  {
    variants: {
      size: {
        sm: 'tw-p-2',
        lg: 'tw-p-3'
      },
      error: {
        true: 'tw-border-destructive-foreground'
      }
    },
    defaultVariants: {
      size: 'lg'
    }
  }
);

const ButtonGroupContext = React.createContext<VariantProps<typeof buttonGroupVariants>>({
  size: 'lg',
  error: false
});

const ButtonGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> &
    VariantProps<typeof buttonGroupVariants>
>(({ className, size, error, children, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root className={cn('tw-flex tw-gap-2', className)} {...props} ref={ref}>
      <ButtonGroupContext.Provider value={{ size, error }}>{children}</ButtonGroupContext.Provider>
    </RadioGroupPrimitive.Root>
  );
});
ButtonGroup.displayName = 'ButtonGroup';

const ButtonGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> &
    VariantProps<typeof buttonGroupVariants>
>(({ className, size, error, ...props }, ref) => {
  const { size: contextSize, error: contextError } = React.useContext(ButtonGroupContext);
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        buttonGroupVariants({ size: size ?? contextSize, error: error ?? contextError, className })
      )}
      {...props}
    />
  );
});
ButtonGroupItem.displayName = 'ButtonGroupItem';

export { ButtonGroup, ButtonGroupItem };
